import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useAronUiStore } from "../store/aronUiStore";
import { AronDock } from "./AronDock";

// Bounds: the dock never gets narrower than this, and always leaves at least
// MIN_CONTENT px for the page on the left.
const MIN_DOCK = 320;
const MIN_CONTENT = 420;

/**
 * Mounted globally in ProtectedLayout, next to the page content column. When a
 * conversation is "wydzielona" (detached) it renders a draggable divider + a
 * resizable right-hand panel holding the ARON dock, so the page shows on the
 * left and the conversation runs alongside it. Desktop-only (md+): the slide
 * divider has no meaning on a phone-width screen. Gated to dlc_03 users.
 */
export function AronDockHost() {
  const user = useAuthStore((s) => s.user);
  const open = useAronUiStore((s) => s.dockOpen);
  // Ta sama rozmowa albo stoi przypięta z boku, albo pływa nad stroną —
  // nigdy jedno i drugie naraz.
  const mode = useAronUiStore((s) => s.dockMode);
  const width = useAronUiStore((s) => s.dockWidth);
  const setWidth = useAronUiStore((s) => s.setDockWidth);
  const [dragging, setDragging] = useState(false);
  // Live width during a drag; committed to the store (persisted) on mouse-up so
  // we don't write to localStorage on every pixel of movement.
  const [liveWidth, setLiveWidth] = useState(width);

  useEffect(() => {
    if (!dragging) setLiveWidth(width);
  }, [width, dragging]);

  const canUse =
    !!user &&
    user.role !== "contractor" &&
    !!user.accessible_dlc_modules?.includes("dlc_03");
  if (!canUse || !open || mode !== "side") return null;

  const clampW = (w: number) =>
    Math.max(MIN_DOCK, Math.min(w, window.innerWidth - MIN_CONTENT));

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    // Dock is anchored right: its width is the gap from the cursor to the edge.
    const onMove = (ev: MouseEvent) =>
      setLiveWidth(clampW(window.innerWidth - ev.clientX));
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragging(false);
      setWidth(clampW(window.innerWidth - ev.clientX));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Przeciągnij, aby zmienić szerokość panelu ARON"
        onMouseDown={startDrag}
        className={`hidden w-1.5 shrink-0 cursor-col-resize border-l border-gray-200 transition-colors hover:bg-red-400 md:block dark:border-gray-700 ${
          dragging ? "bg-red-500" : "bg-gray-100 dark:bg-gray-800"
        }`}
      />
      <aside
        style={{ width: liveWidth }}
        className="hidden shrink-0 flex-col bg-white md:flex dark:bg-gray-900"
      >
        <AronDock />
      </aside>
      {/* While dragging, a full-screen layer keeps the resize cursor and stops
          the pointer being captured by iframes/charts under it. */}
      {dragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
    </>
  );
}
